import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getNotifications, getNotificationReadStatus, markNotificationRead } from '@/lib/api';
import CostTrackerWidget from '@/components/CostTrackerWidget';
import {
  SquaresFour, ChartBar, Kanban, Table, GearSix, SignOut, List, X, Plus, UserCircle, Flask, Bell, Lightbulb, Browser, Gauge
} from '@phosphor-icons/react';

const baseNavItems = [
  { to: '/', icon: ChartBar, label: 'Dashboard' },
  { to: '/kanban', icon: Kanban, label: 'Kanban Board' },
  { to: '/tasks', icon: Table, label: 'Table View' },
  { to: '/ab-tests', icon: Flask, label: 'A/B Testing' },
  { to: '/website-speed', icon: Gauge, label: 'Website Speed' },
  { to: '/pdp-variations', icon: Browser, label: 'PDP Variations' },
];
const adminNavItem = { to: '/admin', icon: GearSix, label: 'Control Panel' };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState([]);
  const isAdmin = user?.role === 'admin';
  const navItems = isAdmin ? [...baseNavItems, adminNavItem] : baseNavItems;

  const loadNotifications = useCallback(async () => {
    try {
      const [nRes, rRes] = await Promise.all([getNotifications(), getNotificationReadStatus()]);
      setNotifications(nRes.data);
      setReadIds(rRes.data);
    } catch (err) {
      console.error('Failed to load notifications:', err.message);
    }
  }, []);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const unreadCount = notifications.filter(n => !readIds.includes(n.id)).length;

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setReadIds(prev => [...prev, id]);
    } catch (err) {
      console.error('Failed to mark notification read:', err.message);
    }
  };

  return (
    <div className="flex h-screen bg-white overflow-hidden" data-testid="app-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#E8E8E8] flex flex-col transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        <div className="h-14 flex items-center justify-between px-5 border-b border-[#E8E8E8]">
          <div className="flex items-center gap-2.5">
            <SquaresFour size={22} weight="bold" className="text-[#32B4AC]" />
            <span className="font-['Manrope'] text-base font-bold tracking-tight text-[#0F0F0F]">TechFlow</span>
          </div>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X size={18} className="text-[#555555]" />
          </button>
        </div>

        <div className="px-4 py-4">
          <button
            data-testid="create-task-btn"
            onClick={() => navigate('/tasks/new')}
            className="w-full h-9 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] transition-colors duration-150 flex items-center justify-center gap-2"
          >
            <Plus size={16} weight="bold" />
            New Task
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-0.5">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-9 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? 'bg-[#F4F4F4] text-[#0F0F0F] font-medium border-l-2 border-[#32B4AC]'
                    : 'text-[#555555] hover:bg-[#F4F4F4] hover:text-[#0F0F0F] border-l-2 border-transparent'
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-[#E8E8E8] p-4">
          <NavLink
            to="/feature-request"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 h-9 rounded-md text-sm transition-colors duration-150 ${
                isActive
                  ? 'bg-[#F4F4F4] text-[#0F0F0F] font-medium border-l-2 border-[#32B4AC]'
                  : 'text-[#555555] hover:bg-[#F4F4F4] hover:text-[#0F0F0F] border-l-2 border-transparent'
              }`
            }
            data-testid="nav-request-feature"
          >
            <Lightbulb size={18} />
            Request Feature
          </NavLink>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 flex items-center justify-between px-6 border-b border-[#E8E8E8] bg-white shrink-0">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <List size={20} className="text-[#0F0F0F]" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            {/* Cost Tracker Widget */}
            <CostTrackerWidget />
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setBellOpen(!bellOpen)}
                className="relative p-1.5 hover:bg-[#F4F4F4] rounded-md transition-colors"
                data-testid="notification-bell-btn"
              >
                <Bell size={20} className="text-[#555555]" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] h-[18px] bg-[#FF3B30] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-unread-count">
                    {unreadCount}
                  </span>
                )}
              </button>

              {bellOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                  <div className="absolute right-0 top-10 w-80 bg-white border border-[#E8E8E8] rounded-md z-50 overflow-hidden" data-testid="notification-dropdown" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                    <div className="px-4 py-3 border-b border-[#E8E8E8]">
                      <h3 className="text-sm font-bold text-[#0F0F0F]">Notifications</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length > 0 ? notifications.map(n => {
                        const isRead = readIds.includes(n.id);
                        return (
                          <div
                            key={n.id}
                            onClick={() => handleMarkRead(n.id)}
                            className={`px-4 py-3 border-b border-[#F4F4F4] cursor-pointer hover:bg-[#F4F4F4] transition-colors ${!isRead ? 'bg-[#32B4AC]/5' : ''}`}
                            data-testid={`notification-item-${n.id}`}
                          >
                            <div className="flex items-start gap-2">
                              {!isRead && <span className="w-2 h-2 rounded-full bg-[#32B4AC] mt-1.5 shrink-0" />}
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm ${!isRead ? 'font-semibold text-[#0F0F0F]' : 'text-[#1A1A1A]'}`}>{n.title}</p>
                                {n.description && <p className="text-xs text-[#555555] mt-0.5 line-clamp-2">{n.description}</p>}
                                <p className="text-[10px] text-[#555555] mt-1">{new Date(n.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                              </div>
                            </div>
                          </div>
                        );
                      }) : (
                        <div className="px-4 py-8 text-center text-xs text-[#555555]">No notifications yet</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* User Profile */}
            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 p-1.5 hover:bg-[#F4F4F4] rounded-md transition-colors"
                data-testid="user-profile-btn"
              >
                <UserCircle size={24} className="text-[#555555]" />
                <span className="text-sm font-medium text-[#0F0F0F] hidden sm:block max-w-[120px] truncate">{user?.name || 'User'}</span>
              </button>

              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                  <div className="absolute right-0 top-10 w-56 bg-white border border-[#E8E8E8] rounded-md z-50 overflow-hidden" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }} data-testid="user-profile-dropdown">
                    <div className="px-4 py-3 border-b border-[#E8E8E8]">
                      <p className="text-sm font-medium text-[#0F0F0F] truncate">{user?.name || 'User'}</p>
                      <p className="text-xs text-[#555555] truncate">{user?.email}</p>
                    </div>
                    <button
                      data-testid="logout-btn"
                      onClick={logout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#555555] hover:bg-[#F4F4F4] hover:text-[#0F0F0F] transition-colors"
                    >
                      <SignOut size={16} />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
